import { Group, Expense, User, ActivityLog } from '../types';
import { db, isFirebaseEnabled, auth } from './firebase';
import { collection, getDocs, getDoc, query, where, updateDoc, doc, arrayUnion, setDoc, orderBy, limit, deleteDoc, writeBatch, onSnapshot } from 'firebase/firestore';

// --------------------------------------------------------------------------
// LOCAL STORAGE KEYS
// --------------------------------------------------------------------------
const KEY_GROUPS = 'paycircle_groups';
const KEY_EXPENSES = 'paycircle_expenses';
const KEY_ACTIVITIES = 'paycircle_activities';
const KEY_USERS = 'paycircle_users';

// --------------------------------------------------------------------------
// DEFAULT DATA
// --------------------------------------------------------------------------
const SEED_GROUPS: Group[] = [];
const SEED_EXPENSES: Expense[] = [];
const SEED_USERS: User[] = [];

// --------------------------------------------------------------------------
// HELPER: SANITIZE DATA FOR FIRESTORE
// --------------------------------------------------------------------------
const sanitize = <T extends object>(obj: T): T => {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }
  if (Array.isArray(obj)) {
    return obj.map(item => (typeof item === 'object' && item !== null ? sanitize(item) : item)) as unknown as T;
  }
  const cleanObj: any = {};
  Object.keys(obj).forEach(key => {
    const val = (obj as any)[key];
    if (val !== undefined) {
      cleanObj[key] = (typeof val === 'object' && val !== null && !(val instanceof Date)) ? sanitize(val) : val;
    }
  });
  return cleanObj as T;
};

// --------------------------------------------------------------------------
// HELPER: RESILIENT LISTENER WITH AUTO-RECONNECT & TOKEN REFRESH
// --------------------------------------------------------------------------
const createResilientListener = <T>(
  subscribeFn: (
    onNext: (snapshot: T) => void,
    onError: (error: any) => void
  ) => () => void,
  onNext: (snapshot: T) => void,
  onFallback: (error: any) => void,
  label: string
): (() => void) => {
  let activeUnsub: (() => void) | null = null;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let isCancelled = false;

  const setupListener = () => {
    if (isCancelled) return;
    try {
      activeUnsub = subscribeFn(
        (snapshot) => {
          if (retryTimer) {
            clearTimeout(retryTimer);
            retryTimer = null;
          }
          onNext(snapshot);
        },
        (error) => {
          console.warn(`[DataService] Listener [${label}] error:`, error);
          onFallback(error);

          // Refresh auth token smoothly using cached token when available
          if (auth?.currentUser) {
            auth.currentUser.getIdToken().catch(tokErr => {
              // Silently handle if Token Service API is restricted in Google Cloud Console
              if (!tokErr?.message?.includes('securetoken.googleapis.com')) {
                console.warn(`[DataService] Auth token refresh on listener error:`, tokErr);
              }
            });
          }

          // Schedule re-subscription retry in background (auto-recovery)
          if (!isCancelled) {
            if (activeUnsub) {
              try { activeUnsub(); } catch (_) { }
              activeUnsub = null;
            }
            retryTimer = setTimeout(() => {
              if (!isCancelled) {
                console.log(`[DataService] Retrying listener subscription [${label}]...`);
                setupListener();
              }
            }, 4000);
          }
        }
      );
    } catch (e) {
      console.warn(`[DataService] Failed to initialize listener [${label}]:`, e);
      onFallback(e);
    }
  };

  setupListener();

  return () => {
    isCancelled = true;
    if (retryTimer) clearTimeout(retryTimer);
    if (activeUnsub) {
      try { activeUnsub(); } catch (_) { }
    }
  };
};

// --------------------------------------------------------------------------
// DATA SERVICE
// --------------------------------------------------------------------------

export const dataService = {

  // --- USERS / FRIENDS ---

  listenToUsers: (userIds: string[], onUpdate: (users: User[]) => void): (() => void) => {
    if (isFirebaseEnabled && db && userIds.length > 0) {
      const uniqueIds = Array.from(new Set(userIds)).filter(Boolean);
      const resultsMap = new Map<string, User>();

      const unsubscribes = uniqueIds.map(userId => {
        const docRef = doc(db, 'users', userId);
        return createResilientListener(
          (next, err) => onSnapshot(docRef, next, err),
          (snapshot: any) => {
            if (snapshot.exists()) {
              resultsMap.set(snapshot.id, { id: snapshot.id, ...(snapshot.data() as any) } as User);
            } else {
              resultsMap.delete(userId);
            }
            onUpdate(Array.from(resultsMap.values()));
          },
          () => {
            const stored = localStorage.getItem(KEY_USERS);
            if (stored) {
              try {
                const cachedUsers: User[] = JSON.parse(stored);
                onUpdate(cachedUsers.filter(u => userIds.includes(u.id)));
              } catch (_) { }
            }
          },
          `user_${userId}`
        );
      });

      return () => {
        unsubscribes.forEach(unsub => unsub());
      };
    }
    const stored = localStorage.getItem(KEY_USERS);
    onUpdate(stored ? JSON.parse(stored) : SEED_USERS);
    return () => { };
  },

  addUser: async (user: User): Promise<User> => {
    // 1. Instantly persist to local storage cache for immediate offline responsiveness
    const stored = localStorage.getItem(KEY_USERS);
    let users: User[] = stored ? JSON.parse(stored) : SEED_USERS;
    const index = users.findIndex(u => u.id === user.id);
    if (index === -1) {
      users.push(user);
    } else {
      users[index] = { ...users[index], ...user };
    }
    localStorage.setItem(KEY_USERS, JSON.stringify(users));

    // 2. Write to Firestore in the background (non-blocking)
    if (isFirebaseEnabled && db && user.id) {
      const safeData = sanitize(user);
      setDoc(doc(db, 'users', user.id), safeData, { merge: true }).catch(err => {
        console.error("Firestore addUser failed:", err);
      });
    }
    return user;
  },

  getUsers: async (): Promise<User[]> => {
    if (isFirebaseEnabled && db) {
      try {
        const q = query(collection(db, 'users'));
        const snapshot = await getDocs(q);
        const users = snapshot.docs.map(doc => ({ id: doc.id, ...(doc.data() as any) } as User));
        localStorage.setItem(KEY_USERS, JSON.stringify(users));
        return users;
      } catch (e) { console.error(e); }
    }
    const stored = localStorage.getItem(KEY_USERS);
    return stored ? JSON.parse(stored) : SEED_USERS;
  },

  updateUser: async (userId: string, data: Partial<User>): Promise<void> => {
    // 1. Update local cache immediately
    const stored = localStorage.getItem(KEY_USERS);
    const users: User[] = stored ? JSON.parse(stored) : SEED_USERS;
    const index = users.findIndex(u => u.id === userId);
    if (index !== -1) {
      users[index] = { ...users[index], ...data };
      localStorage.setItem(KEY_USERS, JSON.stringify(users));

      // Update session storage if current user is me
      const sessionStr = localStorage.getItem('paycircle_session_user');
      if (sessionStr) {
        const session = JSON.parse(sessionStr);
        if (session.id === userId) {
          localStorage.setItem('paycircle_session_user', JSON.stringify(users[index]));
        }
      }
    }

    // 2. Write to Firestore with merge
    if (isFirebaseEnabled && db) {
      const safeData = sanitize(data);
      try {
        await setDoc(doc(db, 'users', userId), safeData, { merge: true });
      } catch (err) {
        console.error("Firestore updateUser failed:", err);
      }
    }
  },

  findUserByEmail: async (email: string): Promise<User | null> => {
    if (isFirebaseEnabled && db) {
      try {
        const q = query(collection(db, 'users'), where('email', '==', email));
        const snapshot = await getDocs(q);
        if (!snapshot.empty) {
          const doc = snapshot.docs[0];
          return { id: doc.id, ...(doc.data() as any) } as User;
        }
      } catch (e) { console.error("Error finding user", e); }
    }
    const users = await dataService.getUsers();
    return users.find(u => u.email.toLowerCase() === email.toLowerCase()) || null;
  },

  addFriendConnection: async (currentUserId: string, friendId: string) => {
    // 1. Update local cache immediately
    const stored = localStorage.getItem(KEY_USERS);
    let users: User[] = stored ? JSON.parse(stored) : SEED_USERS;
    const me = users.find(u => u.id === currentUserId);
    if (me) {
      if (!me.friends) me.friends = [];
      if (!me.friends.includes(friendId)) {
        me.friends.push(friendId);
        localStorage.setItem(KEY_USERS, JSON.stringify(users));

        // Update session storage if current user is me
        const sessionStr = localStorage.getItem('paycircle_session_user');
        if (sessionStr) {
          const session = JSON.parse(sessionStr);
          if (session.id === currentUserId) {
            localStorage.setItem('paycircle_session_user', JSON.stringify(me));
          }
        }
      }
    }

    // 2. Write to Firestore in the background
    if (isFirebaseEnabled && db) {
      const userRef = doc(db, 'users', currentUserId);
      updateDoc(userRef, {
        friends: arrayUnion(friendId)
      }).catch(err => {
        console.error("Firestore addFriendConnection failed:", err);
      });
    }
  },

  // --- GROUPS ---

  listenToGroups: (userId: string, onUpdate: (groups: Group[]) => void): (() => void) => {
    if (isFirebaseEnabled && db) {
      const q = query(collection(db, 'groups'), where('members', 'array-contains', userId));
      return createResilientListener(
        (next, err) => onSnapshot(q, next, err),
        (snapshot: any) => {
          const groups = snapshot.docs.map((doc: any) => ({ id: doc.id, ...(doc.data() as any) } as Group));
          localStorage.setItem(KEY_GROUPS, JSON.stringify(groups));
          onUpdate(groups);
        },
        () => {
          const stored = localStorage.getItem(KEY_GROUPS);
          const allGroups: Group[] = stored ? JSON.parse(stored) : SEED_GROUPS;
          onUpdate(allGroups.filter(g => g.members.includes(userId)));
        },
        `groups_${userId}`
      );
    }
    const stored = localStorage.getItem(KEY_GROUPS);
    const allGroups: Group[] = stored ? JSON.parse(stored) : SEED_GROUPS;
    onUpdate(allGroups.filter(g => g.members.includes(userId)));
    return () => { };
  },

  getGroupById: async (groupId: string): Promise<Group | null> => {
    if (isFirebaseEnabled && db) {
      try {
        const docRef = doc(db, 'groups', groupId);
        const snapshot = await getDoc(docRef);
        if (snapshot.exists()) {
          const group = { id: snapshot.id, ...(snapshot.data() as any) } as Group;
          // Sync to cache
          const stored = localStorage.getItem(KEY_GROUPS);
          const groups: Group[] = stored ? JSON.parse(stored) : SEED_GROUPS;
          const index = groups.findIndex(g => g.id === groupId);
          if (index === -1) groups.push(group);
          else groups[index] = group;
          localStorage.setItem(KEY_GROUPS, JSON.stringify(groups));
          return group;
        }
      } catch (e) { console.error(e); }
    }
    const stored = localStorage.getItem(KEY_GROUPS);
    const allGroups: Group[] = stored ? JSON.parse(stored) : SEED_GROUPS;
    return allGroups.find(g => g.id === groupId) || null;
  },

  listenToGroup: (groupId: string, onUpdate: (group: Group | null) => void): (() => void) => {
    if (isFirebaseEnabled && db) {
      const docRef = doc(db, 'groups', groupId);
      return createResilientListener(
        (next, err) => onSnapshot(docRef, next, err),
        (snapshot: any) => {
          if (snapshot.exists()) {
            const group = { id: snapshot.id, ...(snapshot.data() as any) } as Group;
            const stored = localStorage.getItem(KEY_GROUPS);
            const groups: Group[] = stored ? JSON.parse(stored) : SEED_GROUPS;
            const index = groups.findIndex(g => g.id === groupId);
            if (index === -1) groups.push(group);
            else groups[index] = group;
            localStorage.setItem(KEY_GROUPS, JSON.stringify(groups));
            onUpdate(group);
          } else {
            onUpdate(null);
          }
        },
        () => {
          const stored = localStorage.getItem(KEY_GROUPS);
          const allGroups: Group[] = stored ? JSON.parse(stored) : SEED_GROUPS;
          onUpdate(allGroups.find(g => g.id === groupId) || null);
        },
        `group_${groupId}`
      );
    }
    const stored = localStorage.getItem(KEY_GROUPS);
    const allGroups: Group[] = stored ? JSON.parse(stored) : SEED_GROUPS;
    onUpdate(allGroups.find(g => g.id === groupId) || null);
    return () => { };
  },

  addGroup: async (group: Group): Promise<Group> => {
    // 1. Instantly update local cache
    const stored = localStorage.getItem(KEY_GROUPS);
    const groups: Group[] = stored ? JSON.parse(stored) : SEED_GROUPS;
    if (!groups.find(g => g.id === group.id)) {
      groups.push(group);
      localStorage.setItem(KEY_GROUPS, JSON.stringify(groups));
    }

    // 2. Write to Firestore with merge
    if (isFirebaseEnabled && db) {
      const safeData = sanitize(group);
      try {
        await setDoc(doc(db, 'groups', group.id), safeData, { merge: true });
      } catch (err) {
        console.error("Firestore addGroup failed:", err);
        if (navigator.onLine && auth?.currentUser) {
          throw err;
        }
      }
    }
    return group;
  },

  updateGroup: async (groupId: string, data: Partial<Group>): Promise<void> => {
    // 1. Update local cache immediately
    const stored = localStorage.getItem(KEY_GROUPS);
    const groups: Group[] = stored ? JSON.parse(stored) : SEED_GROUPS;
    const index = groups.findIndex(g => g.id === groupId);
    if (index !== -1) {
      groups[index] = { ...groups[index], ...data };
      localStorage.setItem(KEY_GROUPS, JSON.stringify(groups));
    }

    // 2. Write to Firestore with merge
    if (isFirebaseEnabled && db) {
      const safeData = sanitize(data);
      try {
        await setDoc(doc(db, 'groups', groupId), safeData, { merge: true });
      } catch (err) {
        console.error("Firestore updateGroup failed:", err);
        if (navigator.onLine && auth?.currentUser) {
          throw err;
        }
      }
    }
  },

  deleteGroup: async (groupId: string): Promise<void> => {
    // 1. Update local cache immediately
    const stored = localStorage.getItem(KEY_GROUPS);
    const groups: Group[] = stored ? JSON.parse(stored) : SEED_GROUPS;
    const filtered = groups.filter(g => g.id !== groupId);
    localStorage.setItem(KEY_GROUPS, JSON.stringify(filtered));

    // 2. Write to Firestore
    if (isFirebaseEnabled && db) {
      try {
        await deleteDoc(doc(db, 'groups', groupId));
      } catch (err) {
        console.error("Firestore deleteGroup failed:", err);
        if (navigator.onLine && auth?.currentUser) {
          throw err;
        }
      }
    }
  },

  // --- EXPENSES ---

  listenToExpenses: (userId: string, groupIds: string[], onUpdate: (expenses: Expense[]) => void): (() => void) => {
    if (isFirebaseEnabled && db) {
      const uniqueGroupIds = Array.from(new Set(groupIds)).filter(Boolean);
      const queries = [
        query(collection(db, 'expenses'), where('payerId', '==', userId)),
        query(collection(db, 'expenses'), where('splitWith', 'array-contains', userId)),
        query(collection(db, 'expenses'), where('groupMembers', 'array-contains', userId))
      ];

      if (uniqueGroupIds.length > 0) {
        for (let i = 0; i < uniqueGroupIds.length; i += 30) {
          const chunk = uniqueGroupIds.slice(i, i + 30);
          queries.push(query(collection(db, 'expenses'), where('groupId', 'in', chunk)));
        }
      }

      // Initialize resultsMap with current local cached expenses to preserve optimistic and non-conflicting items
      const stored = localStorage.getItem(KEY_EXPENSES);
      const cachedExpenses: Expense[] = stored ? JSON.parse(stored) : SEED_EXPENSES;
      const resultsMap = new Map<string, Expense>();
      cachedExpenses.forEach(e => {
        if (
          e.payerId === userId ||
          (e.splitWith && e.splitWith.includes(userId)) ||
          (e.groupMembers && e.groupMembers.includes(userId)) ||
          (e.groupId && uniqueGroupIds.includes(e.groupId))
        ) {
          resultsMap.set(e.id, e);
        }
      });

      const unsubscribes = queries.map((q, qIndex) => {
        return createResilientListener(
          (next, err) => onSnapshot(q, next, err),
          (snapshot: any) => {
            snapshot.docs.forEach((doc: any) => {
              resultsMap.set(doc.id, { id: doc.id, ...(doc.data() as any) } as Expense);
            });

            snapshot.docChanges().forEach((change: any) => {
              if (change.type === 'removed') {
                resultsMap.delete(change.doc.id);
              }
            });

            const expensesList = Array.from(resultsMap.values()).sort((a, b) => b.date.localeCompare(a.date));
            localStorage.setItem(KEY_EXPENSES, JSON.stringify(expensesList));
            onUpdate(expensesList);
          },
          () => {
            const currentStored = localStorage.getItem(KEY_EXPENSES);
            const allExpenses: Expense[] = currentStored ? JSON.parse(currentStored) : SEED_EXPENSES;
            const filtered = allExpenses.filter(e =>
              e.payerId === userId ||
              (e.splitWith && e.splitWith.includes(userId)) ||
              (e.groupMembers && e.groupMembers.includes(userId)) ||
              (e.groupId && groupIds.includes(e.groupId))
            );
            onUpdate(filtered);
          },
          `expenses_q${qIndex}`
        );
      });

      return () => {
        unsubscribes.forEach(unsub => unsub());
      };
    }
    const stored = localStorage.getItem(KEY_EXPENSES);
    const allExpenses: Expense[] = stored ? JSON.parse(stored) : SEED_EXPENSES;
    const filtered = allExpenses.filter(e =>
      e.payerId === userId ||
      (e.splitWith && e.splitWith.includes(userId)) ||
      (e.groupMembers && e.groupMembers.includes(userId)) ||
      (e.groupId && groupIds.includes(e.groupId))
    );
    onUpdate(filtered);
    return () => { };
  },

  addExpense: async (expense: Expense): Promise<Expense> => {
    let expenseToSave: Expense = { ...expense };
    // If expense belongs to a group, ensure groupMembers is denormalized
    if (expenseToSave.groupId && (!expenseToSave.groupMembers || expenseToSave.groupMembers.length === 0)) {
      const storedGroups = localStorage.getItem(KEY_GROUPS);
      if (storedGroups) {
        try {
          const groups: Group[] = JSON.parse(storedGroups);
          const found = groups.find(g => g.id === expenseToSave.groupId);
          if (found && found.members) {
            expenseToSave.groupMembers = found.members;
          }
        } catch (_) { }
      }
    }

    // 1. Update local cache immediately
    const stored = localStorage.getItem(KEY_EXPENSES);
    const expenses: Expense[] = stored ? JSON.parse(stored) : SEED_EXPENSES;
    const existingIndex = expenses.findIndex(e => e.id === expenseToSave.id);
    if (existingIndex === -1) {
      expenses.unshift(expenseToSave);
    } else {
      expenses[existingIndex] = expenseToSave;
    }
    localStorage.setItem(KEY_EXPENSES, JSON.stringify(expenses));

    // 2. Write to Firestore reliably
    if (isFirebaseEnabled && db) {
      const safeData = sanitize(expenseToSave);
      try {
        await setDoc(doc(db, 'expenses', expenseToSave.id), safeData, { merge: true });
      } catch (err) {
        console.error("Firestore addExpense failed:", err);
        if (navigator.onLine && auth?.currentUser) {
          throw err;
        }
      }
    }
    return expenseToSave;
  },

  updateExpense: async (expenseId: string, data: Partial<Expense>): Promise<void> => {
    // 1. Update local cache immediately with full document merge
    const stored = localStorage.getItem(KEY_EXPENSES);
    const expenses: Expense[] = stored ? JSON.parse(stored) : SEED_EXPENSES;
    const index = expenses.findIndex(e => e.id === expenseId);
    let mergedExpense: Expense;
    if (index !== -1) {
      mergedExpense = { ...expenses[index], ...data, updatedAt: new Date().toISOString() };
      expenses[index] = mergedExpense;
      localStorage.setItem(KEY_EXPENSES, JSON.stringify(expenses));
    } else {
      mergedExpense = { id: expenseId, ...data } as Expense;
    }

    // If expense belongs to a group, ensure groupMembers is denormalized
    if (mergedExpense.groupId && (!mergedExpense.groupMembers || mergedExpense.groupMembers.length === 0)) {
      const storedGroups = localStorage.getItem(KEY_GROUPS);
      if (storedGroups) {
        try {
          const groups: Group[] = JSON.parse(storedGroups);
          const found = groups.find(g => g.id === mergedExpense.groupId);
          if (found && found.members) {
            mergedExpense.groupMembers = found.members;
          }
        } catch (_) { }
      }
    }

    // 2. Write to Firestore with merge
    if (isFirebaseEnabled && db) {
      const safeData = sanitize(mergedExpense);
      try {
        await setDoc(doc(db, 'expenses', expenseId), safeData, { merge: true });
      } catch (err) {
        console.error("Firestore updateExpense failed:", err);
        if (navigator.onLine && auth?.currentUser) {
          throw err;
        }
      }
    }
  },

  deleteExpense: async (expenseId: string): Promise<void> => {
    // 1. Update local cache immediately
    const stored = localStorage.getItem(KEY_EXPENSES);
    const expenses: Expense[] = stored ? JSON.parse(stored) : SEED_EXPENSES;
    const filtered = expenses.filter(e => e.id !== expenseId);
    localStorage.setItem(KEY_EXPENSES, JSON.stringify(filtered));

    // 2. Write to Firestore
    if (isFirebaseEnabled && db) {
      try {
        await deleteDoc(doc(db, 'expenses', expenseId));
      } catch (err) {
        console.error("Firestore deleteExpense failed:", err);
        if (navigator.onLine && auth?.currentUser) {
          throw err;
        }
      }
    }
  },

  deleteExpensesByGroupId: async (groupId: string): Promise<void> => {
    // 1. Update local cache immediately
    const stored = localStorage.getItem(KEY_EXPENSES);
    const expenses: Expense[] = stored ? JSON.parse(stored) : SEED_EXPENSES;
    const filtered = expenses.filter(e => e.groupId !== groupId);
    localStorage.setItem(KEY_EXPENSES, JSON.stringify(filtered));

    // 2. Write to Firestore in the background
    if (isFirebaseEnabled && db) {
      const activeDb = db;
      const q = query(collection(activeDb, 'expenses'), where('groupId', '==', groupId));
      getDocs(q).then((snapshot) => {
        const batch = writeBatch(activeDb);
        snapshot.docs.forEach(d => batch.delete(d.ref));
        return batch.commit();
      }).catch(err => {
        console.error("Firestore deleteExpensesByGroupId failed:", err);
      });
    }
  },

  // --- ACTIVITIES ---

  listenToActivities: (userId: string, onUpdate: (activities: ActivityLog[]) => void): (() => void) => {
    if (isFirebaseEnabled && db) {
      const q = query(collection(db, 'activities'), where('relatedUserIds', 'array-contains', userId), orderBy('timestamp', 'desc'), limit(50));
      return createResilientListener(
        (next, err) => onSnapshot(q, next, err),
        (snapshot: any) => {
          const acts = snapshot.docs.map((doc: any) => ({ id: doc.id, ...(doc.data() as any) } as ActivityLog));
          localStorage.setItem(KEY_ACTIVITIES, JSON.stringify(acts));
          onUpdate(acts);
        },
        () => {
          const stored = localStorage.getItem(KEY_ACTIVITIES);
          const allActivities: ActivityLog[] = stored ? JSON.parse(stored) : [];
          onUpdate(allActivities.filter(a => a.relatedUserIds?.includes(userId)).sort((a, b) => b.timestamp.localeCompare(a.timestamp)));
        },
        `activities_${userId}`
      );
    }
    const stored = localStorage.getItem(KEY_ACTIVITIES);
    const allActivities: ActivityLog[] = stored ? JSON.parse(stored) : [];
    onUpdate(allActivities.filter(a => a.relatedUserIds?.includes(userId)).sort((a, b) => b.timestamp.localeCompare(a.timestamp)));
    return () => { };
  },

  logActivity: async (activity: ActivityLog): Promise<void> => {
    // 1. Update local cache immediately
    const stored = localStorage.getItem(KEY_ACTIVITIES);
    let activities: ActivityLog[] = stored ? JSON.parse(stored) : [];
    if (!activities.find(a => a.id === activity.id)) {
      activities.unshift(activity);
      localStorage.setItem(KEY_ACTIVITIES, JSON.stringify(activities.slice(0, 100)));
    }

    // 2. Write to Firestore in the background
    if (isFirebaseEnabled && db) {
      const safeData = sanitize(activity);
      setDoc(doc(db, 'activities', activity.id), safeData).catch(err => {
        console.error("Firestore logActivity failed:", err);
      });
    }
  },

  migrateGuestData: async (guestId: string, targetUserId: string): Promise<void> => {
    if (!guestId || !targetUserId || guestId === targetUserId) return;

    // 1. Migrate Groups
    try {
      const storedGroups = localStorage.getItem(KEY_GROUPS);
      if (storedGroups) {
        const groups: Group[] = JSON.parse(storedGroups);
        let changed = false;
        groups.forEach(g => {
          if (g.members && g.members.includes(guestId)) {
            g.members = Array.from(new Set(g.members.map(m => (m === guestId ? targetUserId : m))));
            changed = true;
            if (isFirebaseEnabled && db) {
              setDoc(doc(db, 'groups', g.id), sanitize(g), { merge: true }).catch(console.error);
            }
          }
        });
        if (changed) {
          localStorage.setItem(KEY_GROUPS, JSON.stringify(groups));
        }
      }
    } catch (e) {
      console.error("[DataService] Group migration error:", e);
    }

    // 2. Migrate Expenses
    try {
      const storedExpenses = localStorage.getItem(KEY_EXPENSES);
      if (storedExpenses) {
        const expenses: Expense[] = JSON.parse(storedExpenses);
        let changed = false;
        expenses.forEach(e => {
          let expenseChanged = false;
          if (e.payerId === guestId) {
            e.payerId = targetUserId;
            expenseChanged = true;
          }
          if (e.splitWith && e.splitWith.includes(guestId)) {
            e.splitWith = Array.from(new Set(e.splitWith.map(s => (s === guestId ? targetUserId : s))));
            expenseChanged = true;
          }
          if (e.splits && e.splits[guestId] !== undefined) {
            e.splits[targetUserId] = e.splits[guestId];
            delete e.splits[guestId];
            expenseChanged = true;
          }
          if (expenseChanged) {
            changed = true;
            if (isFirebaseEnabled && db) {
              setDoc(doc(db, 'expenses', e.id), sanitize(e), { merge: true }).catch(console.error);
            }
          }
        });
        if (changed) {
          localStorage.setItem(KEY_EXPENSES, JSON.stringify(expenses));
        }
      }
    } catch (e) {
      console.error("[DataService] Expense migration error:", e);
    }
  }
};
